import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Text } from 'rebass';
import { mapStageToFund } from 'components/organisms/contactDetail/contactInfoWorkDate';
import TruncatedText from '../truncatedText';

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${({ split }) =>
    split &&
    css`
      tr > td:first-child {
        max-width: 50%;
        width: auto;
        white-space: nowrap;
      }

      tr > td:last-child {
        width: 100%;
      }
    `}
`;

export const Thead = styled.thead`
  border-bottom: 2px solid;
  border-color: ${({ theme }) => theme.colors.border.subtle};
  white-space: nowrap;
`;

export const Row = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;

export const Cell = styled.td`
  vertical-align: baseline;
  text-overflow: ellipsis;
  padding: 8px 8px 8px 0px;
  overflow: hidden;
  overflow-wrap: break-word;
  text-align: right;

  &.left {
    text-align: left;
  }

  &:first-child {
    text-align: left;
  }
`;

export const LeftCell = styled(Cell)`
  text-align: left;
`;

const CompanyDataTable = ({ company }) => {
  const companyStage = mapStageToFund(company.stage);
  return (
    <Table split>
      <tbody>
        {company.domain && (
          <Row>
            <Cell>
              <Text fontSize="14px">Domain:</Text>
            </Cell>
            <LeftCell>
              <Text fontSize="14px">{company.domain}</Text>
            </LeftCell>
          </Row>
        )}
        {company.locations && company.locations.length > 0 && (
          <Row>
            <Cell>
              <Text fontSize="14px">Locations:</Text>
            </Cell>
            <LeftCell>
              <TruncatedText text={company.locations.map((location) => location.name).join(', ')} maxLength={200} />
            </LeftCell>
          </Row>
        )}
        {companyStage && (
          <Row>
            <Cell>
              <Text fontSize="14px">Stage:</Text>
            </Cell>
            <LeftCell>
              <Text fontSize="14px">{companyStage}</Text>
            </LeftCell>
          </Row>
        )}
        {company.org_type && (
          <Row>
            <Cell>
              <Text fontSize="14px">Organization type:</Text>
            </Cell>
            <LeftCell>
              <Text fontSize="14px">{company.org_type}</Text>
            </LeftCell>
          </Row>
        )}
        {company.industryTags && company.industryTags.length > 0 && (
          <Row>
            <Cell>
              <Text fontSize="14px">Industries:</Text>
            </Cell>
            <LeftCell>
              <TruncatedText text={company.industryTags.map((industry) => industry).join(', ')} maxLength={80} />
            </LeftCell>
          </Row>
        )}
        {!!company.approxEmployees && (
          <Row>
            <Cell>
              <Text fontSize="14px">Company size:</Text>
            </Cell>
            <LeftCell>
              <Text fontSize="14px">{company.approxEmployees?.toLocaleString()}</Text>
            </LeftCell>
          </Row>
        )}

        {company.organizationCompetitors && company.organizationCompetitors.length > 0 && (
          <Row>
            <Cell>
              <Text fontSize="14px">Competitors:</Text>
            </Cell>
            <LeftCell>
              <TruncatedText
                text={company.organizationCompetitors.map((competitor) => competitor.name).join(', ')}
                maxLength={200}
              />
            </LeftCell>
          </Row>
        )}
      </tbody>
    </Table>
  );
};

CompanyDataTable.propTypes = {
  company: PropTypes.shape({
    domain: PropTypes.string,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ),
    stage: PropTypes.string,
    org_type: PropTypes.string,
    industryTags: PropTypes.arrayOf(PropTypes.string),
    approxEmployees: PropTypes.string,
    organizationCompetitors: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default CompanyDataTable;
